import _ from 'lodash';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useForm, UseFormProps, UseFormReturn, FieldValues } from 'react-hook-form';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import {
  setFormValues,
  clearForm,
  toggleTransient,
  setTransientFields,
  TRANSIENT,
  TRANSIENT_FIELDS,
} from 'careand-redux/slices/rhfForms';

// Mounted hook instances per form name. Several components can share one form
// name (DocumentDetails and NewPrescription both use
// CPPUtils.getFormName('prescription', patientId)), and only the last one out
// may delete the shared draft.
const mountedInstances = new Map<string, number>();

/**
 * A wrapper around useForm that syncs form state to Redux via the rhfForms slice.
 * the forms are persisted through ReduxPersist
 *
 * - `options.transient` - (default: false) uses redux to store the form but does not persist across refreshes
 * - `options.transientFields` - specific fields kept in redux but excluded from persistence, for
 *   forms that mix draft data worth restoring with session-only state (e.g. edit-mode state)
 * - `options.destroyOnUnmount` - (default: true) deletes the form from redux on unmount
 */
export function useReduxForm<T extends FieldValues>(
  formName: string,
  options: UseFormProps<T> & {
    transient?: boolean;
    transientFields?: string[];
    destroyOnUnmount?: boolean;
  } = {},
): UseFormReturn<T> {
  const dispatch = useAppDispatch();
  const persistedEntry = useAppSelector((state) => state.rhfForms[formName]) as
    | Record<string, unknown>
    | undefined;
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the last values this hook wrote to Redux, to distinguish external updates
  const lastSyncedRef = useRef<string>('');
  const hasAppliedPersistedRef = useRef(false);
  const prevFormNameRef = useRef(formName);
  // Values of the outgoing form, captured before RHF is repopulated when
  // formName changes on a mounted hook
  const carriedValuesRef = useRef<Record<string, unknown> | null>(null);
  // Unmount cleanup must see the latest options, not the first render's closure
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // The slice keeps its persistence markers inside the values object; they must
  // never reach RHF or they become real form fields and pollute submit payloads
  const persistedValues = useMemo(() => {
    if (!persistedEntry) return undefined;
    const values = _.omit(persistedEntry, [TRANSIENT, TRANSIENT_FIELDS]) as unknown as T;
    // an entry holding nothing but markers carries no values to apply
    return _.isEmpty(values) ? undefined : values;
  }, [persistedEntry]);

  const cancelPendingSync = useCallback(() => {
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
      throttleRef.current = null;
    }
  }, []);

  const methods = useForm<T>({
    ..._.omit(options, ['transient', 'transientFields', 'destroyOnUnmount']),
    // Always use original defaults so reset() clears to empty state
  });

  // formName can change on a mounted hook (ChatWindow switching conversation);
  // treat it as a fresh form instead of carrying the old values into it
  const formNameChanged = prevFormNameRef.current !== formName;
  if (formNameChanged) {
    carriedValuesRef.current = methods.getValues();
    prevFormNameRef.current = formName;
    hasAppliedPersistedRef.current = false;
    lastSyncedRef.current = '';
  }

  // if persisted values exist, apply them (not as defaultValues)
  if (!hasAppliedPersistedRef.current) {
    if (persistedValues) {
      methods.reset(_.cloneDeep(persistedValues) as UseFormProps<T>['defaultValues'] as T, {
        keepDefaultValues: true,
      });
    } else if (formNameChanged) {
      methods.reset();
    }
    hasAppliedPersistedRef.current = true;
  }

  const resetOverride = useCallback(
    ((args: any, resetOptions?: any) => {
      // Cancel any pending throttled sync so stale values don't overwrite the reset
      cancelPendingSync();
      if (!args) {
        methods.reset();
        // reset() notifies the watch subscription, which schedules a fresh sync;
        // leaving it armed would resurrect the form we are about to clear
        cancelPendingSync();
        lastSyncedRef.current = '';
        dispatch(clearForm(formName));
        // clearForm drops the persistence markers along with the entry — put
        // them back, or a session-only form silently starts being persisted
        const { transient, transientFields } = optionsRef.current;
        if (transient) {
          dispatch(toggleTransient({ formName, transient: true }));
        }
        if (transientFields?.length) {
          dispatch(setTransientFields({ formName, fields: transientFields }));
        }
      } else {
        methods.reset(args, resetOptions);
        cancelPendingSync();
        // Clone: Immer freezes whatever reaches the store, and callers keep
        // mutating the object they passed (getValues() -> reset() -> push())
        const values = _.cloneDeep(args) as Record<string, unknown>;
        // Record this write so the external-change effect doesn't mistake our own
        // reset for an external update and re-reset, wiping setValue calls made
        // right after (e.g. edit flows that reset then repopulate fields)
        lastSyncedRef.current = JSON.stringify(values);
        dispatch(
          setFormValues({
            formName,
            values,
          }),
        );
      }
    }) as typeof methods.reset,
    [methods.reset, formName, dispatch, cancelPendingSync],
  );

  useEffect(() => {
    if (options.transient) {
      dispatch(toggleTransient({ formName, transient: true }));
    }
  }, [dispatch, formName, options.transient]);

  const transientFieldsKey = options.transientFields?.join(',');
  useEffect(() => {
    if (options.transientFields?.length) {
      dispatch(setTransientFields({ formName, fields: options.transientFields }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formName, transientFieldsKey]);

  // Watch all values and sync to Redux (throttled)
  useEffect(() => {
    const subscription = methods.watch(() => {
      cancelPendingSync();
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null;
        const values = methods.getValues() as Record<string, unknown>;
        lastSyncedRef.current = JSON.stringify(values);
        dispatch(setFormValues({ formName, values }));
      }, 300);
    });
    return () => {
      subscription.unsubscribe();
      cancelPendingSync();
    };
  }, [methods, methods.watch, dispatch, formName, cancelPendingSync]);

  // Sync initial values to Redux on mount
  useEffect(() => {
    const sync = (values: Record<string, unknown>) => {
      lastSyncedRef.current = JSON.stringify(values);
      dispatch(setFormValues({ formName, values }));
    };
    sync(methods.getValues() as Record<string, unknown>);
    mountedInstances.set(formName, (mountedInstances.get(formName) ?? 0) + 1);

    return () => {
      const remaining = (mountedInstances.get(formName) ?? 1) - 1;
      if (remaining > 0) {
        mountedInstances.set(formName, remaining);
      } else {
        mountedInstances.delete(formName);
      }
      // when formName changed, RHF already holds the incoming form's values
      const outgoingValues = carriedValuesRef.current;
      carriedValuesRef.current = null;
      if (optionsRef.current.destroyOnUnmount ?? true) {
        // another instance is still editing this form; its draft isn't ours to delete
        if (remaining <= 0) {
          dispatch(clearForm(formName));
        }
      } else {
        sync((outgoingValues ?? methods.getValues()) as Record<string, unknown>);
      }
    };
  }, [formName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect external Redux changes and sync back to RHF
  useEffect(() => {
    if (!persistedValues) return;
    // Skip if a local change hasn't synced to Redux yet — resetting now would wipe it
    if (throttleRef.current) return;
    const serialized = JSON.stringify(persistedValues);
    if (serialized !== lastSyncedRef.current) {
      lastSyncedRef.current = serialized;
      methods.reset(_.cloneDeep(persistedValues) as UseFormProps<T>['defaultValues'] as T, {
        keepDefaultValues: true,
      });
    }
  }, [persistedValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const methodsOverride = useMemo(() => {
    const override = {
      ...methods,
      reset: resetOverride,
    };
    // RHF swaps in a new formState proxy on every state change; spreading it
    // once would freeze errors/isSubmitting at the first render's snapshot
    Object.defineProperty(override, 'formState', {
      get: () => methods.formState,
      enumerable: true,
      configurable: true,
    });
    return override;
  }, [methods, resetOverride]);

  return methodsOverride;
}

export { clearForm } from 'careand-redux/slices/rhfForms';
export { setFormValues } from 'careand-redux/slices/rhfForms';
